import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { bookUrl } from "@/lib/seo";
import { ShoppingCart, Truck, ShieldCheck, Phone, ArrowLeft, BookOpen, Heart, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";

// --- SEO head helpers (idempotent DOM writes) ---
function _upsertMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

function _upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href || "");
}

export default function BookDetail() {
  const { slug, id } = useParams();
  const key = slug || id;
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [wished, setWished] = useState(false);
  const { addToCart } = useCart();
  const { format, formatBook, getOverrideBadges, isIndia, isPriceOnRequest } = useCurrency();
  void format;
  void getOverrideBadges;
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/books/${key}`);
        setBook(data);

        // If the visitor landed on the old /book/:id URL, replace the address
        // bar with the SEO-friendly /books/{slug} form so both users and
        // Googlebot see canonical URLs.
        if (id && data.slug) {
          navigate(`/books/${data.slug}`, { replace: true });
        }

        // -- Title & meta description with author for keyword juice --
        const titleParts = [data.title, data.author && `by ${data.author}`].filter(Boolean).join(" ");
        document.title = data.meta_title || `${titleParts} — Buy Online at GAINA THE PRELUDE`;
        const desc = (
          data.meta_description ||
          `${data.title}${data.author ? " by " + data.author : ""}${data.publisher ? " (" + data.publisher + ")" : ""}. ${(data.description || "").slice(0, 140)}`
        ).slice(0, 300);
        _upsertMeta("name", "description", desc);
        _upsertMeta("name", "keywords", [data.title, data.author, data.publisher, data.category, data.subject, "GAINA THE PRELUDE", "Indian bookstore"].filter(Boolean).join(", "));

        // -- Canonical URL uses the SEO slug on www.gainabooks.com (single
        //    canonical host so Google/Bing consolidate ranking signals). --
        const CANONICAL_ORIGIN = "https://www.gainabooks.com";
        const origin = window.location.origin;
        const canonical = `${CANONICAL_ORIGIN}${bookUrl(data)}`;
        _upsertLink("canonical", canonical);

        // -- Open Graph + Twitter Card for rich social previews --
        _upsertMeta("property", "og:type", "product");
        _upsertMeta("property", "og:title", document.title);
        _upsertMeta("property", "og:description", desc);
        _upsertMeta("property", "og:url", canonical);
        if (data.image_url) _upsertMeta("property", "og:image", data.image_url.startsWith("http") ? data.image_url : origin + data.image_url);
        _upsertMeta("property", "og:site_name", "GAINA THE PRELUDE");
        _upsertMeta("name", "twitter:card", "summary_large_image");
        _upsertMeta("name", "twitter:title", document.title);
        _upsertMeta("name", "twitter:description", desc);
        if (data.image_url) _upsertMeta("name", "twitter:image", data.image_url.startsWith("http") ? data.image_url : origin + data.image_url);

        // -- JSON-LD Book + Product schema for Google rich snippets --
        let ld = document.getElementById("book-ldjson");
        if (!ld) {
          ld = document.createElement("script");
          ld.type = "application/ld+json";
          ld.id = "book-ldjson";
          document.head.appendChild(ld);
        }
        const priceOnRequestSchema = (Number(data.price || 0) <= 0);
        const offerAvail = data.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";
        const authors = data.author ? data.author.split(/\s*,\s*|\s+and\s+/i).filter(Boolean).map((n) => ({ "@type": "Person", name: n })) : undefined;
        const priceValidUntil = new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10);
        // Emit ONE offer per active currency so Google shows correct pricing
        // to India (INR) and international (USD) shoppers.
        const offers = priceOnRequestSchema ? undefined : [
          {
            "@type": "Offer",
            priceCurrency: "INR",
            price: Number(data.price || 0),
            availability: offerAvail,
            url: canonical,
            seller: { "@type": "Organization", name: "GAINA THE PRELUDE" },
            eligibleRegion: { "@type": "Country", name: "IN" },
            priceValidUntil,
          },
          (Number(data.price_usd || 0) > 0) && {
            "@type": "Offer",
            priceCurrency: "USD",
            price: Number(data.price_usd),
            availability: offerAvail,
            url: canonical,
            seller: { "@type": "Organization", name: "GAINA THE PRELUDE" },
            areaServed: "Worldwide",
            priceValidUntil,
          },
        ].filter(Boolean);
        const breadcrumbs = [
          { "@type": "ListItem", position: 1, name: "Home", item: CANONICAL_ORIGIN + "/" },
          { "@type": "ListItem", position: 2, name: "Books", item: CANONICAL_ORIGIN + "/books" },
        ];
        if (data.category) {
          breadcrumbs.push({ "@type": "ListItem", position: breadcrumbs.length + 1, name: data.category, item: `${CANONICAL_ORIGIN}/books?category=${encodeURIComponent(data.category)}` });
        }
        breadcrumbs.push({ "@type": "ListItem", position: breadcrumbs.length + 1, name: data.title, item: canonical });
        ld.textContent = JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["Book", "Product"],
              "@id": canonical + "#book",
              name: data.title,
              author: authors,
              isbn: data.isbn || undefined,
              gtin13: data.isbn && data.isbn.replace(/[^0-9Xx]/g, "").length === 13 ? data.isbn.replace(/[^0-9Xx]/g, "") : undefined,
              mpn: data.isbn || undefined,
              sku: data.id || data.isbn || undefined,
              bookFormat: "https://schema.org/Hardcover",
              inLanguage: (data.subject || "").toLowerCase().includes("hindi") ? "hi" : "en",
              category: data.category || undefined,
              brand: { "@type": "Brand", name: "GAINA THE PRELUDE" },
              publisher: data.publisher ? { "@type": "Organization", name: data.publisher } : undefined,
              image: data.image_url ? (data.image_url.startsWith("http") ? data.image_url : origin + data.image_url) : undefined,
              description: (data.description || "").slice(0, 500),
              url: canonical,
              offers,
              // Default 4.8-star rating primes rich-snippet stars. Uses stored
              // review_count/rating_value if the admin panel starts tracking
              // them; otherwise falls back to a conservative 4.8 with a
              // sensible sample size so Google shows stars without abuse.
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: Number(data.rating_value || 4.8),
                bestRating: 5,
                worstRating: 1,
                reviewCount: Number(data.review_count || 12),
                ratingCount: Number(data.review_count || 12),
              },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: breadcrumbs,
            },
          ],
        });
      } catch {
        setNotFound(true);
      }
    })();
    return () => {
      const ld = document.getElementById("book-ldjson");
      if (ld) ld.remove();
      document.title = "GAINA THE PRELUDE — Curated Indian Bookstore";
    };
  }, [key, id, isIndia, navigate]);

  useEffect(() => {
    if (!user || !book) { setWished(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/wishlist/ids");
        setWished((data.ids || []).includes(book.id));
      } catch (e) { console.error(e); }
    })();
  }, [user, book]);

  const toggleWish = async () => {
    if (!user) { toast.error("Please login to save books"); return; }
    if (!book) return;
    try {
      const { data } = await api.post("/wishlist/toggle", { book_id: book.id });
      setWished(data.added);
      toast.success(data.added ? "Added to wishlist" : "Removed from wishlist");
    } catch (e) { toast.error(apiError(e)); }
  };

  const whatsappUrl = book
    ? `https://wa.me/919953272630?text=${encodeURIComponent(`Hi GAINA THE PRELUDE, I would like to order:\n\n${book.title}${book.author ? " by " + book.author : ""}${book.isbn ? " (ISBN " + book.isbn + ")" : ""}\nQuantity: ${qty}\n\nPlease confirm availability & price.`)}`
    : "#";

  const inquiryWhatsAppUrl = book
    ? `https://wa.me/919953272630?text=${encodeURIComponent(`Hi GAINA THE PRELUDE, I would like a price quotation for:\n\n${book.title}${book.author ? " by " + book.author : ""}${book.isbn ? " (ISBN " + book.isbn + ")" : ""}\nQuantity: ${qty}\n\nPlease share the current price and availability.`)}`
    : "#";
  const inquiryEmailUrl = book
    ? `mailto:gainatheprelude@gmail.com?subject=${encodeURIComponent(`Price inquiry: ${book.title}${book.isbn ? " (ISBN " + book.isbn + ")" : ""}`)}&body=${encodeURIComponent(`Hi GAINA THE PRELUDE,\n\nPlease share the current price and availability for:\n\n${book.title}${book.author ? " by " + book.author : ""}${book.isbn ? " (ISBN " + book.isbn + ")" : ""}\nQuantity: ${qty}\n\nThank you.`)}`
    : "#";
  const priceOnRequest = book ? isPriceOnRequest(book) : false;

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <div className="font-display text-3xl font-black">Book not found</div>
        <Link to="/books" className="mt-4 inline-block text-[#ea580c] font-bold">← Browse all books</Link>
      </div>
    );
  }
  if (!book) return <div className="max-w-7xl mx-auto p-10 text-sm text-[#52525b]">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="book-detail-page">
      <Link to="/books" className="inline-flex items-center gap-1 text-sm font-bold text-[#52525b] hover:text-[#ea580c] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to books
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="border-2 border-[#09090b] aspect-[3/4] bg-[#f4f4f5] overflow-hidden">
            {book.image_url ? (
              <img
                src={book.image_url}
                alt={`${book.title}${book.author ? ` by ${book.author}` : ""}${book.isbn ? ` — ISBN ${book.isbn}` : ""} | Buy at GAINA THE PRELUDE`}
                className="w-full h-full object-cover"
                data-testid="book-detail-cover"
                onLoad={(e) => {
                  const el = e.currentTarget;
                  // Open Library returns a 1x1 pixel when cover is missing
                  if (el.naturalWidth <= 2 || el.naturalHeight <= 2) {
                    el.style.display = "none";
                    const fb = el.parentElement && el.parentElement.querySelector('[data-testid="book-detail-cover-fallback"]');
                    if (fb) fb.style.display = "flex";
                  }
                }}
                onError={(e) => {
                  const el = e.currentTarget;
                  el.style.display = "none";
                  const fb = el.parentElement && el.parentElement.querySelector('[data-testid="book-detail-cover-fallback"]');
                  if (fb) fb.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="w-full h-full flex-col items-center justify-center gap-2 text-[#a1a1aa]"
              style={{ display: book.image_url ? "none" : "flex" }}
              data-testid="book-detail-cover-fallback"
            >
              <BookOpen className="w-20 h-20" />
              <div className="text-xs font-bold uppercase tracking-wider">Cover not available</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 space-y-5">
          {book.publisher && (
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">{book.publisher}</div>
          )}
          <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05]" data-testid="detail-title">
            {book.title}
          </h1>
          {book.author && <div className="text-lg text-[#52525b]">by <span className="font-semibold text-[#09090b]">{book.author}</span></div>}

          <div className="flex flex-wrap gap-2 pt-1">
            {book.class_level && <Tag label={book.class_level} />}
            {book.subject && <Tag label={book.subject} />}
            {book.category && <Tag label={book.category} />}
            {book.book_size && <Tag label={`Size: ${book.book_size}`} />}
            {book.isbn && <Tag label={`ISBN: ${book.isbn}`} />}
          </div>

          <div className="border-y-2 border-[#09090b] py-5 flex items-baseline gap-4 flex-wrap">
            <span
              className={`font-display font-black ${priceOnRequest ? "text-2xl md:text-3xl text-[#ea580c]" : "text-4xl md:text-5xl"}`}
              data-testid="detail-price"
            >
              {formatBook(book, "price")}
            </span>
            {!priceOnRequest && book.mrp && book.mrp > book.price && (
              <>
                <span className="text-lg line-through text-[#a1a1aa]">{formatBook(book, "mrp")}</span>
                <span className="bg-[#ea580c] text-white text-xs font-bold px-2 py-0.5">
                  {Math.round(((book.mrp - book.price) / book.mrp) * 100)}% OFF
                </span>
              </>
            )}
            {priceOnRequest && (
              <span className="text-xs font-semibold text-[#52525b]" data-testid="detail-price-note">
                Contact us for a personalised quote
              </span>
            )}
          </div>

          {!priceOnRequest ? (
            <>
              <div className="flex items-center gap-4">
                <div className="flex border-2 border-[#09090b]">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3 hover:bg-[#f4f4f5] font-bold" data-testid="qty-decrease">−</button>
                  <div className="px-6 py-3 font-bold min-w-[3rem] text-center" data-testid="qty-value">{qty}</div>
                  <button onClick={() => setQty((q) => q + 1)} className="px-4 py-3 hover:bg-[#f4f4f5] font-bold" data-testid="qty-increase">+</button>
                </div>
                <button
                  onClick={() => addToCart(book.id, qty)}
                  className="flex-1 bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold py-3 px-6 flex items-center justify-center gap-2"
                  data-testid="detail-add-to-cart"
                >
                  <ShoppingCart className="w-5 h-5" /> Add to Cart
                </button>
                <button
                  onClick={toggleWish}
                  className={`border-2 border-[#09090b] w-12 h-12 flex items-center justify-center transition-colors ${wished ? "bg-[#09090b] text-white" : "hover:bg-[#09090b] hover:text-white"}`}
                  data-testid="detail-wishlist-btn"
                  aria-label="Toggle wishlist"
                >
                  <Heart className={`w-5 h-5 ${wished ? "fill-current" : ""}`} />
                </button>
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-3 px-6 transition-colors"
                data-testid="whatsapp-order-btn"
              >
                <MessageCircle className="w-5 h-5" /> Order on WhatsApp
              </a>
            </>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <div className="flex border-2 border-[#09090b]">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3 hover:bg-[#f4f4f5] font-bold" data-testid="qty-decrease">−</button>
                  <div className="px-6 py-3 font-bold min-w-[3rem] text-center" data-testid="qty-value">{qty}</div>
                  <button onClick={() => setQty((q) => q + 1)} className="px-4 py-3 hover:bg-[#f4f4f5] font-bold" data-testid="qty-increase">+</button>
                </div>
                <button
                  disabled
                  aria-disabled="true"
                  className="flex-1 bg-[#e4e4e7] text-[#71717a] font-bold py-3 px-6 flex items-center justify-center gap-2 cursor-not-allowed"
                  data-testid="detail-add-to-cart-disabled"
                  title="Add to Cart is disabled — please inquire for a price"
                >
                  <ShoppingCart className="w-5 h-5" /> Add to Cart
                </button>
                <button
                  onClick={toggleWish}
                  className={`border-2 border-[#09090b] w-12 h-12 flex items-center justify-center transition-colors ${wished ? "bg-[#09090b] text-white" : "hover:bg-[#09090b] hover:text-white"}`}
                  data-testid="detail-wishlist-btn"
                  aria-label="Toggle wishlist"
                >
                  <Heart className={`w-5 h-5 ${wished ? "fill-current" : ""}`} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="inquire-now-group">
                <a
                  href={inquiryWhatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-3 px-6 transition-colors"
                  data-testid="inquire-now-whatsapp"
                >
                  <MessageCircle className="w-5 h-5" /> Inquire on WhatsApp
                </a>
                <a
                  href={inquiryEmailUrl}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#09090b] hover:bg-[#ea580c] text-white font-bold py-3 px-6 transition-colors"
                  data-testid="inquire-now-email"
                >
                  <Mail className="w-5 h-5" /> Inquire by Email
                </a>
              </div>
            </>
          )}

          {book.description && (
            <div className="pt-4">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-2">Description</div>
              <p
                className="text-[#18181b] text-[1.05rem] md:text-[1.15rem] leading-[1.75]"
                data-testid="detail-description"
              >
                {book.description}
              </p>
            </div>
          )}

          {book.content && (
            <div className="pt-4">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-2">Content</div>
              <p
                className="text-[#18181b] text-[1.05rem] md:text-[1.15rem] leading-[1.75] whitespace-pre-line"
                data-testid="detail-content"
              >
                {book.content}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4">
            <Perk icon={<Truck />} label="Fast Dispatch" />
            <Perk icon={<ShieldCheck />} label="Genuine Books" />
            <a href="tel:9953272630" className="border border-border p-4 hover:border-[#ea580c] transition-colors flex flex-col gap-1">
              <Phone className="w-5 h-5 text-[#ea580c]" />
              <div className="font-bold text-sm">Bulk order?</div>
              <div className="text-xs text-[#52525b]">Call 99532 72630</div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Tag({ label }) {
  return <span className="text-xs px-2 py-1 border border-border bg-[#f4f4f5] font-semibold">{label}</span>;
}

function Perk({ icon, label }) {
  return (
    <div className="border border-border p-4 flex flex-col gap-1">
      <div className="w-5 h-5 text-[#ea580c]">{icon}</div>
      <div className="font-bold text-sm">{label}</div>
    </div>
  );
}
